// @ts-check
/** @typedef {import('./types').BookmarkDrawerAppState} BookmarkDrawerAppState */

import {Component} from 'panel';
import {extend} from 'common/util';
import {registerMPElement} from 'common/util/register-element';
import {nameToInitials} from 'common/util/string';
import {mpColors} from 'common/util/colors';

import template from './index.jade';
import css from './index.styl?inline';

const MPConfirm = customElements.get(`mp-confirm`);
class BookmarkConfirm extends MPConfirm {
  connectedCallback() {
    if (this.initialized) {
      return;
    }

    if (!this.elementMoved) {
      this.elementMoved = true;
      document.body.style.position = `relative`;
      // @ts-ignore
      document.body.appendChild(this);
      requestAnimationFrame(() => {
        // TODO this is a hack ensuring that the confirm modal will be in front of the drawer overlay
        this.el
          .querySelector(`mp-modal`)
          .el // reach inside mp-modal's (shadow) DOM
          .querySelector(`style`).textContent += `.mp-modal-stage { z-index: 9500; }`;
      });
    }

    if (!this.initialized) {
      super.connectedCallback(...arguments);
    }
  }
}
registerMPElement(`bookmark-mp-confirm`, BookmarkConfirm);

// -------------------------------------------------------------------------------

/**
 * @extends Component<BookmarkDrawerAppState>
 */
class BookmarkDrawer extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'delete-enabled': `boolean`,
      loading: `boolean`,
      'ootb-default-reports-enabled': `boolean`,
      open: `boolean`,
      'saved-reports': `json`,
      'user-id': `string`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        reportsDrawer: {
          searchFilter: ``,
          userFilter: `all`,
        },
      },
      helpers: {
        nameToInitials,
        changeSearchFilter: (ev) => this.updateDrawer({searchFilter: ev.target.value}),
        changeUserFilter: (ev) => this.updateDrawer({userFilter: ev.detail.selected}),
        getReportIconColor: (icon) => {
          switch (icon) {
            case `bar`:
              return mpColors[`$blue1`];
            case `funnel-steps`:
              return mpColors[`$blue1`];
            case `analysis-logarithmic`:
              return mpColors[`$blue1`];
            case `line`:
              return mpColors[`$blue1`];
            case `table`:
              return mpColors[`$blue1`];
            default:
              return mpColors[`$gray4`];
          }
        },
        clickDelete: (ev, report) => {
          ev.stopPropagation();
          this.updateDrawer({confirmDelete: report});
        },
        handleTableRowClick: ({detail}) => {
          this.helpers.close();
          this.dispatchEvent(new CustomEvent(`select`, {detail: {report: detail.row}}));
        },
        close: () => {
          this.updateDrawer({nameFilter: ``});
          this.dispatchEvent(new CustomEvent(`close`));
        },
        deleteReport: (report) => {
          this.dispatchEvent(new CustomEvent(`delete`, {detail: {report}}));
          this.updateDrawer({confirmDelete: null});
        },
        handleConfirmChange: (visibility) => {
          if (visibility !== `open`) {
            this.updateDrawer({confirmDelete: null});
          }
        },
        showOotbDefaultReports: () => {
          const userID = this.attr(`user-id`);
          const userHasDefaultReports =
            Object.values(this.attr(`saved-reports`)).filter((bm) => String(bm.userID) === userID && bm.is_default)
              .length > 0;
          return this.attr(`ootb-default-reports-enabled`) && userHasDefaultReports;
        },
        reportsForDisplay: () => {
          const drawer = this.state.reportsDrawer;
          const userID = this.attr(`user-id`);
          let reports = Object.values(this.attr(`saved-reports`));
          if (drawer.userFilter === `all`) {
            reports = reports.filter((bm) => String(bm.userID) === userID || !bm.is_default);
          } else if (drawer.userFilter === `default`) {
            reports = reports.filter((bm) => String(bm.userID) === userID && bm.is_default);
          } else if (drawer.userFilter === `yours`) {
            reports = reports.filter((bm) => String(bm.userID) === userID && !bm.is_default);
          }

          return reports;
        },
      },
    };
  }

  updateDrawer(attrs) {
    this.update({reportsDrawer: extend(this.state.reportsDrawer, attrs)});
  }
}

registerMPElement(`mp-bookmark-drawer`, BookmarkDrawer);

export default BookmarkDrawer;
